import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Upload, Pencil, Trash2, Clock, Users } from "lucide-react";
import { toast } from "sonner";

const empty = {
  first_name: "",
  middle_name: "",
  last_name: "",
  full_name: "",
  address: "",
  age: "",
  gender: "",
  contact_number: "",
  birthday: "",
  notes: "",
  next_visit_date: "",
};

// Split a legacy full name into first / middle / last for editing existing patients.
function splitName(p) {
  if (p.first_name || p.middle_name || p.last_name) {
    return { first_name: p.first_name || "", middle_name: p.middle_name || "", last_name: p.last_name || "" };
  }
  const parts = String(p.full_name || "").trim().split(/\s+/);
  if (parts.length <= 1) return { first_name: parts[0] || "", middle_name: "", last_name: "" };
  if (parts.length === 2) return { first_name: parts[0], middle_name: "", last_name: parts[1] };
  return { first_name: parts.slice(0, -2).join(" "), middle_name: parts[parts.length - 2], last_name: parts[parts.length - 1] };
}

function ageFromBirthday(bday) {
  if (!bday) return "";
  const d = new Date(bday);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 200 ? age : "";
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function Patients() {
  const { user } = useAuth();
  const isDoctor = user?.role === "doctor";

  const [items, setItems] = useState([]);
  const [todayQueue, setTodayQueue] = useState([]);
  const [activeTab, setActiveTab] = useState(isDoctor ? "today" : "all");

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [todayLoading, setTodayLoading] = useState(false);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients${search ? `?q=${encodeURIComponent(search)}` : ""}`);
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  const loadToday = async () => {
    if (!isDoctor) return;
    setTodayLoading(true);
    try {
      const { data } = await api.get("/queue/doctor/today");
      setTodayQueue(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setTodayLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoctor]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const filteredTodayQueue = useMemo(() => {
    const search = q.trim().toLowerCase();
    if (!search) return todayQueue;

    return todayQueue.filter((row) => {
      const patient = row.patient_snapshot || {};
      return (
        (patient.full_name || "").toLowerCase().includes(search) ||
        (patient.contact_number || "").toLowerCase().includes(search) ||
        (row.reason || "").toLowerCase().includes(search) ||
        (row.status || "").toLowerCase().includes(search)
      );
    });
  }, [q, todayQueue]);

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      ...splitName(p),
      full_name: p.full_name || "",
      address: p.address || "",
      age: p.age ?? "",
      gender: p.gender || "",
      contact_number: p.contact_number || "",
      birthday: p.birthday || "",
      notes: p.notes || "",
      next_visit_date: p.next_visit_date || "",
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const first = (form.first_name || "").trim();
      const middle = (form.middle_name || "").trim();
      const last = (form.last_name || "").trim();
      const payload = {
        ...form,
        first_name: first,
        middle_name: middle,
        last_name: last,
        full_name: [first, middle, last].filter(Boolean).join(" "),
        age: form.age === "" ? null : Number(form.age) || form.age,
      };
      if (editingId) {
        await api.put(`/patients/${editingId}`, payload);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", payload);
        toast.success("Patient added");
      }
      setOpen(false);
      load(q);
      loadToday();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this patient?")) return;
    await api.delete(`/patients/${id}`);
    toast.success("Patient archived");
    load(q);
    loadToday();
  };

  const displayCount = isDoctor && activeTab === "today" ? filteredTodayQueue.length : items.length;

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="patients-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1
            className="text-3xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: "Work Sans" }}
          >
            Patients
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {displayCount} record{displayCount === 1 ? "" : "s"}
          </p>
        </div>

        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="btn-bulk-upload-patients">
            <Link to="/patients/bulk-upload">
              <Upload size={14} className="mr-2" /> Bulk Upload
            </Link>
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-patient">
            <Plus size={14} className="mr-2" /> Add Patient
          </Button>
        </div>
      </div>

      {isDoctor ? (
        <Card className="p-2 border-slate-200 mb-4 inline-flex gap-1">
          <button
            type="button"
            onClick={() => setActiveTab("today")}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 ${
              activeTab === "today"
                ? "bg-blue-600 text-white"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            data-testid="tab-today-patients"
          >
            <Clock size={14} /> Today's Patients
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 ${
              activeTab === "all"
                ? "bg-blue-600 text-white"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            data-testid="tab-all-patients"
          >
            <Users size={14} /> All Patients
          </button>
        </Card>
      ) : null}

      <Card className="p-4 border-slate-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={
              isDoctor && activeTab === "today"
                ? "Search today's patients by name, reason, or status..."
                : "Search by name or contact..."
            }
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="patient-search"
          />
        </div>
      </Card>

      {isDoctor && activeTab === "today" ? (
        <Card className="border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="font-semibold text-slate-900">Today's Assigned Patients</div>
            <div className="text-xs text-slate-500 mt-0.5">Patients assigned to you from today's queue.</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white text-slate-500 uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Time</th>
                  <th className="px-4 py-3 text-left font-semibold">Patient</th>
                  <th className="px-4 py-3 text-left font-semibold">Age</th>
                  <th className="px-4 py-3 text-left font-semibold">Gender</th>
                  <th className="px-4 py-3 text-left font-semibold">Visit Reason</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {todayLoading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                      Loading today's patients...
                    </td>
                  </tr>
                ) : filteredTodayQueue.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                      No assigned patients for today.
                    </td>
                  </tr>
                ) : (
                  filteredTodayQueue.map((row) => {
                    const patient = row.patient_snapshot || {};
                    return (
                      <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`today-patient-row-${row.id}`}>
                        <td className="px-4 py-3 text-slate-700 text-xs">{fmtTime(row.created_at)}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          <Link to={`/patients/${row.patient_id}`} className="hover:text-blue-700 hover:underline">
                            {patient.full_name || "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{patient.age || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{patient.gender || "—"}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[320px] truncate" title={row.reason || ""}>
                          {row.reason || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {row.status || "waiting"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/patients/${row.patient_id}`}>Open</Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Age</th>
                  <th className="px-4 py-3 text-left font-semibold">Gender</th>
                  <th className="px-4 py-3 text-left font-semibold">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold">Address</th>
                  <th className="px-4 py-3 text-left font-semibold">Next Visit</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                      No patients yet.
                    </td>
                  </tr>
                ) : (
                  items.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`patient-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        <Link
                          to={`/patients/${p.id}`}
                          className="text-slate-900 hover:text-blue-700 hover:underline"
                          data-testid={`patient-link-${p.id}`}
                        >
                          {p.full_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{p.age || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{p.gender || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 mono text-xs">{p.contact_number || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 truncate max-w-[260px]">{p.address || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 text-xs mono">{p.next_visit_date || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`patient-edit-${p.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`patient-delete-${p.id}`}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="patient-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Patient" : "Add Patient"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First Name *</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="patient-input-first-name"
              />
            </div>
            <div>
              <Label>Middle Name</Label>
              <Input
                value={form.middle_name}
                onChange={(e) => setForm({ ...form, middle_name: e.target.value })}
                placeholder="Optional"
                data-testid="patient-input-middle-name"
              />
            </div>
            <div>
              <Label>Surname *</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="patient-input-last-name"
              />
            </div>
            <div>
              <Label>Age</Label>
              <Input
                type="number"
                value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                disabled={ageFromBirthday(form.birthday) !== ""}
                data-testid="patient-input-age"
              />
              {ageFromBirthday(form.birthday) !== "" && (
                <div className="text-xs text-slate-400 mt-1">Auto-calculated from birthday</div>
              )}
            </div>
            <div>
              <Label>Gender</Label>
              <Input
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                data-testid="patient-input-gender"
              />
            </div>
            <div>
              <Label>Contact Number</Label>
              <Input
                value={form.contact_number}
                onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                data-testid="patient-input-contact"
              />
            </div>
            <div>
              <Label>Birthday</Label>
              <Input
                type="date"
                value={form.birthday}
                onChange={(e) => {
                  const birthday = e.target.value;
                  const computed = ageFromBirthday(birthday);
                  setForm((f) => ({ ...f, birthday, ...(computed !== "" ? { age: computed } : {}) }));
                }}
                placeholder="YYYY-MM-DD"
                data-testid="patient-input-birthday"
              />
            </div>
            <div>
              <Label>Next Visit Date</Label>
              <Input
                type="date"
                value={form.next_visit_date}
                onChange={(e) => setForm({ ...form, next_visit_date: e.target.value })}
                data-testid="patient-input-next-visit"
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="patient-input-address"
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="patient-input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="patient-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
